import React from 'react';
import { ExternalLink, Clock, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { NewsItem } from '../types';

interface NewsFeedProps {
  news: NewsItem[];
  symbol: string;
}

const NewsFeed: React.FC<NewsFeedProps> = ({ news, symbol }) => {
  const getSentimentIcon = (sentiment: NewsItem['sentiment']) => {
    switch (sentiment) {
      case 'bullish':
        return <TrendingUp className="text-neon-green" size={16} />;
      case 'bearish':
        return <TrendingDown className="text-neon-red" size={16} />;
      default:
        return <Minus className="text-terminal-muted" size={16} />;
    }
  };

  const getSentimentColor = (sentiment: NewsItem['sentiment']) => {
    switch (sentiment) {
      case 'bullish':
        return 'text-neon-green';
      case 'bearish':
        return 'text-neon-red';
      default:
        return 'text-terminal-muted';
    }
  };

  const getSentimentBg = (sentiment: NewsItem['sentiment']) => {
    switch (sentiment) {
      case 'bullish':
        return 'bg-neon-green/10 border-neon-green/30';
      case 'bearish':
        return 'bg-neon-red/10 border-neon-red/30';
      default:
        return 'bg-terminal-border/50 border-terminal-border';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="terminal-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-terminal-text">Latest News</h3>
        <span className="text-sm text-terminal-muted">{symbol}</span>
      </div>

      {news.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-terminal-muted mb-2">📰</div>
          <p className="text-terminal-muted">No recent news available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {news.slice(0, 3).map((item, index) => (
            <article
              key={item.id}
              className={`p-4 rounded-lg border transition-all duration-200 hover:bg-terminal-border/20 ${getSentimentBg(item.sentiment)}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {getSentimentIcon(item.sentiment)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-terminal-text font-medium leading-tight line-clamp-2">
                      {item.title}
                    </h4>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-1 text-terminal-muted hover:text-neon-blue transition-colors"
                      title="Read full article"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </div>
                  
                  <p className="text-sm text-terminal-muted leading-relaxed mb-3 line-clamp-2">
                    {item.summary}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                      <span className="text-terminal-muted font-medium">
                        {item.source}
                      </span>
                      <div className="flex items-center gap-1 text-terminal-muted">
                        <Clock size={12} />
                        <span>{formatTimeAgo(item.publishedAt)}</span>
                      </div>
                    </div>
                    
                    <div className={`flex items-center gap-1 ${getSentimentColor(item.sentiment)}`}>
                      <span className="capitalize font-medium">
                        {item.sentiment}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {news.length > 3 && (
        <div className="mt-6 pt-4 border-t border-terminal-border">
          <button className="w-full py-2 px-4 bg-terminal-border hover:bg-terminal-muted/20 text-terminal-text rounded-lg transition-colors text-sm font-medium">
            View All News ({news.length})
          </button>
        </div>
      )}

      {/* News Summary Stats */}
      {news.length > 0 && (
        <div className="mt-6 pt-4 border-t border-terminal-border">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-neon-green">
                {news.filter(n => n.sentiment === 'bullish').length}
              </div>
              <div className="text-xs text-terminal-muted">Bullish</div>
            </div>
            <div>
              <div className="text-lg font-bold text-terminal-muted">
                {news.filter(n => n.sentiment === 'neutral').length}
              </div>
              <div className="text-xs text-terminal-muted">Neutral</div>
            </div>
            <div>
              <div className="text-lg font-bold text-neon-red">
                {news.filter(n => n.sentiment === 'bearish').length}
              </div>
              <div className="text-xs text-terminal-muted">Bearish</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsFeed;